/** 企微认证 AuthResponse → 平台会话 VO */

export const ROLE_CHINESE_NAMES = {
  BOSS: '老板',
  SUPERADMIN: '超级管理员',
  ADMIN: '管理员',
  PLATFORM_ADMIN: '平台管理员',
  DESIGNER_LEADER: '设计主管',
  SHOP_KEEPER: '店长',
  STORE_MANAGER: '店长',
  SALESPERSON: '销售',
  DESIGNER: '设计师',
  FACTORY: '工厂',
  FINANCE: '财务',
  CUSTOMER_SERVICE: '客服',
  SERVICE: '客服',
};

const ADMIN_ROLES = new Set(['BOSS', 'SUPERADMIN', 'ADMIN', 'PLATFORM_ADMIN', 'ROOT']);
const MANAGER_ROLES = new Set(['SHOP_KEEPER', 'STORE_MANAGER', 'MANAGER', 'SHOP_MANAGER', 'LEADER']);
const DESIGNER_ROLES = new Set(['DESIGNER_LEADER', 'DESIGNER', 'ART', 'MEIGONG']);
const SERVICE_ROLES = new Set(['SALESPERSON', 'CUSTOMER_SERVICE', 'SERVICE', 'CS', 'KEFU', 'FACTORY', 'FINANCE']);

function normalizeRole(role) {
  return String(role || '')
    .trim()
    .toUpperCase()
    .replace(/^ROLE_/, '');
}

/**
 * 后端 roles → 登录页角色键：platform_admin | store_manager | designer | customer_service
 */
export function mapBackendRolesToLoginRole(roles = []) {
  const list = (Array.isArray(roles) ? roles : []).map(normalizeRole).filter(Boolean);
  if (list.some(r => ADMIN_ROLES.has(r))) return 'platform_admin';
  if (list.some(r => MANAGER_ROLES.has(r))) return 'store_manager';
  if (list.some(r => DESIGNER_ROLES.has(r))) return 'designer';
  if (list.some(r => SERVICE_ROLES.has(r))) return 'customer_service';
  return 'designer';
}

export function mapAuthResponseToSession(dto = {}) {
  const roles = Array.isArray(dto.roles) ? dto.roles.map(String) : [];
  const loginRole = mapBackendRolesToLoginRole(roles);
  const primaryRole = roles[0] ? normalizeRole(roles[0]) : '';
  const roleTitle = ROLE_CHINESE_NAMES[primaryRole] || '';

  return {
    token: dto.token || '',
    tokenType: dto.tokenType || 'Bearer',
    userId: dto.userId != null ? dto.userId : null,
    userName: String(dto.userName || dto.username || '').trim() || '用户',
    roles,
    primaryRole,
    roleTitle,
    weChatWorkAccount: dto.weChatWorkAccount || '',
    expiresIn: dto.expiresIn ?? 0,
    loginRole,
  };
}

/** 解析 JWT payload（仅解码，不验签） */
export function decodeJwtPayload(token) {
  const raw = String(token || '').trim().replace(/^Bearer\s+/i, '');
  const parts = raw.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = typeof atob === 'function'
      ? atob(padded)
      : '';
    if (!json) return null;
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

/** 从 JWT 构建登录会话（用于 URL token 免扫码登录） */
export function mapJwtPayloadToAuthSession(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  if (payload.exp != null && Number(payload.exp) * 1000 <= Date.now()) return null;

  const roles = Array.isArray(payload.roles)
    ? payload.roles.map(String)
    : payload.role
      ? [String(payload.role)]
      : [];

  return mapAuthResponseToSession({
    token: String(token || '').trim().replace(/^Bearer\s+/i, ''),
    userId: payload.userId ?? payload.sub ?? null,
    userName: payload.userName || payload.username || payload.name || '',
    roles,
    weChatWorkAccount: payload.weChatWorkAccount || '',
  });
}

